import { t } from "@lingui/macro";
import { useBreakpoint } from "@stylingcv/hooks";
import type { SectionKey } from "@stylingcv/schema";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@stylingcv/ui";
import { create } from "zustand";

import { useToast } from "@/client/hooks/use-toast";
import { useResumeStore } from "@/client/stores/resume";

// Create a dedicated store for section deletion
type DeleteSectionStore = {
  isOpen: boolean;
  sectionId: SectionKey | null;
  open: (id: SectionKey) => void;
  close: () => void;
};

export const useDeleteSectionStore = create<DeleteSectionStore>((set) => ({
  isOpen: false,
  sectionId: null,
  open: (id) => {
    set({ isOpen: true, sectionId: id });
  },
  close: () => {
    set({ isOpen: false, sectionId: null });
  },
}));

export const DeleteSectionDialog = () => {
  const { isOpen, sectionId, close } = useDeleteSectionStore();
  const removeSection = useResumeStore((state) => state.removeSection);
  const { toast } = useToast();
  const { isMobile } = useBreakpoint();

  // Check if section is a core section (not custom)
  const isCoreSection = sectionId ? !sectionId.startsWith("custom.") : false;
  const isCustomSection = sectionId ? sectionId.startsWith("custom.") : false;

  const onConfirm = () => {
    if (!sectionId) return;

    try {
      console.log("Removing section with ID:", sectionId);
      removeSection(sectionId);
      toast({
        variant: "default",
        title: isCustomSection ? t`Section deleted successfully` : t`Section hidden successfully`,
        description: isCustomSection
          ? t`The custom section has been permanently removed.`
          : t`The section is now hidden. You can restore it from the visibility toggle.`,
      });
      close();
    } catch (error) {
      console.error("Error deleting section:", error);
      toast({
        variant: "error",
        title: isCustomSection ? t`Failed to delete section` : t`Failed to hide section`,
        description: String(error),
      });
    }
  };

  // Mobile version using Dialog for better visibility
  if (isMobile) {
    return (
      <Dialog open={isOpen} onOpenChange={close}>
        <DialogContent className="fixed left-1/2 top-1/2 z-[99999] w-[95vw] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-background p-6 shadow-2xl">
          <DialogHeader className="space-y-4 text-center">
            <DialogTitle
              className={`text-lg font-semibold ${isCustomSection ? "text-red-600" : "text-orange-600"}`}
            >
              {isCustomSection ? t`Delete Section?` : t`Hide Section?`}
            </DialogTitle>

            <DialogDescription className="text-base leading-relaxed">
              {isCustomSection
                ? t`This will permanently delete the custom section and all its content. This action cannot be undone.`
                : t`This will hide the section from your resume. You can show it again anytime from the section settings.`}
            </DialogDescription>

            {isCoreSection && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950/30">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  {t`Note: Core sections are never permanently deleted - only hidden. You can restore them anytime.`}
                </p>
              </div>
            )}
          </DialogHeader>

          <DialogFooter className="flex flex-col gap-3 pt-4">
            <Button variant="outline" className="h-12 w-full font-medium" onClick={close}>
              {t`Cancel`}
            </Button>
            <Button
              variant={isCustomSection ? "destructive" : "default"}
              className={`h-12 w-full font-medium ${
                isCustomSection
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-orange-600 text-white hover:bg-orange-700"
              }`}
              onClick={onConfirm}
            >
              {isCustomSection ? t`Delete Section` : t`Hide Section`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Desktop version using AlertDialog
  return (
    <AlertDialog open={isOpen} onOpenChange={close}>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isCustomSection
              ? t`Are you sure you want to delete this section?`
              : t`Are you sure you want to hide this section?`}
          </AlertDialogTitle>

          <AlertDialogDescription>
            {isCustomSection
              ? t`This will permanently delete the custom section and all its content. This action cannot be undone.`
              : t`This will hide the section from your resume. You can show it again anytime from the section settings.`}
          </AlertDialogDescription>

          {isCoreSection && (
            <div className="mt-4 text-sm font-semibold text-info">
              {t`Note: Core sections are never permanently deleted - only hidden. You can restore them anytime.`}
            </div>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
          <AlertDialogAction variant={isCustomSection ? "error" : "default"} onClick={onConfirm}>
            {isCustomSection ? t`Delete Section` : t`Hide Section`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
